import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { FOUNDER } from "@/data/services";

const CHIPS = [
  { text: "40+ Campaigns", hindi: "अभियान", delay: 0 },
  { text: "Indore, MP", hindi: "इंदौर", delay: 1.2 },
  { text: "Since 2019", hindi: "2019 से", delay: 0.6 },
];

export default function FounderVisual() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 120, damping: 20 });
  const sy = useSpring(my, { stiffness: 120, damping: 20 });

  const ringX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const ringY = useTransform(sy, [-0.5, 0.5], [-14, 14]);
  const photoX = useTransform(sx, [-0.5, 0.5], [12, -12]);
  const photoY = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-8, 8]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [6, -6]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="mx-auto w-full max-w-[520px]" data-testid="founder-visual">
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        className="relative aspect-square w-full select-none overflow-hidden rounded-[2rem]"
        style={{ perspective: 1200 }}
      >
        {/* halo + spinning chakra */}
        <motion.div style={{ x: ringX, y: ringY }} className="absolute inset-[12%]">
          <div className="absolute inset-0 rounded-full bg-[conic-gradient(from_210deg,#2f6bff,#ff3d8a,#ff7a1a,#12a150,#2f6bff)] opacity-80 shadow-[0_0_120px_-20px_rgba(255,61,138,0.6)]" />
          <div className="absolute inset-[18%] rounded-full bg-ink/40 blur-2xl" />
          <motion.span
            className="absolute inset-[-10%] rounded-full border border-dashed border-saffron/40"
            animate={{ rotate: -360 }}
            transition={{ duration: 60, ease: "linear", repeat: Infinity }}
          />
        </motion.div>

        {/* cutout photo breaking out of the circle */}
        <motion.div
          style={{ x: photoX, y: photoY, rotateX, rotateY, transformStyle: "preserve-3d" }}
          className="absolute inset-x-[8%] bottom-0 top-[6%]"
        >
          <motion.img
            src="/founder-cutout.png"
            alt={FOUNDER.name}
            data-testid="founder-photo"
            initial={{ opacity: 0, y: 60, scale: 0.94 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="h-full w-full object-contain object-bottom grayscale contrast-[1.08] drop-shadow-[0_30px_50px_rgba(0,0,0,0.6)]"
            style={{
              WebkitMaskImage: "linear-gradient(to bottom, #000 78%, transparent 100%)",
              maskImage: "linear-gradient(to bottom, #000 78%, transparent 100%)",
            }}
            draggable={false}
          />
        </motion.div>
      </div>

      {/* name plate + chips — kept clear of the portrait */}
      <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="tricolor-line mb-3 block h-1 w-16 rounded-full" />
          <p className="font-display text-2xl font-bold uppercase tracking-tight text-paper" data-testid="founder-name">{FOUNDER.name}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-saffron">{FOUNDER.role}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="founder-chips">
          {CHIPS.map((c) => (
            <motion.div
              key={c.text}
              className="rounded-full border border-white/15 bg-coal/70 px-4 py-2"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 4, delay: c.delay, repeat: Infinity, ease: "easeInOut" }}
              data-testid={`founder-chip-${c.text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper">{c.text}</p>
              <p className="font-hindi text-xs leading-none text-saffron">{c.hindi}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
